"use client";

import { Calendar, Clock, MapPin } from "lucide-react";

import { Checkbox } from "@punchup/ui-common/shadcn/ui/checkbox";
import { ShowWithComedian } from "@punchup/data/legacy";
import { cn } from "@punchup/utils/cn";
import dayjs from "@punchup/utils/dayjs";

interface Props {
  show: ShowWithComedian;
  isSelected: boolean;
  isDisabled: boolean;
  onToggle: (showId: string) => void;
}

export default function ShowSelectRow({
  show,
  isSelected,
  isDisabled,
  onToggle,
}: Props) {
  const showDatetime = dayjs(show.datetime);
  const todayDatetime = dayjs();

  const dayLabel = todayDatetime.isSame(showDatetime, "day")
    ? "Today"
    : showDatetime.format("dddd");
  const dateLabel = showDatetime.format("MMM D");
  const dayDateLabel = `${dayLabel}, ${dateLabel}`;
  const timeLabel = showDatetime.format("h:mma");
  const title = show.title || show.venue || "Untitled Show";

  const handleClick = () => {
    if (!isDisabled) {
      onToggle(show.id);
    }
  };

  return (
    <button
      type="button"
      onClick={handleClick}
      disabled={isDisabled}
      className={cn(
        "w-full flex items-center gap-x-4 p-4 rounded-lg border transition-colors text-left",
        isDisabled
          ? "bg-gray-50 border-gray-200 cursor-not-allowed opacity-60"
          : isSelected
            ? "border-primary hover:bg-gray-50"
            : "border-gray-200 hover:border-gray-300 hover:bg-gray-50"
      )}
    >
      <div className="flex flex-col gap-y-1 flex-1 min-w-0">
        <p
          className={cn(
            "flex flex-row gap-x-2 font-medium truncate",
            isDisabled ? "text-gray-500" : "text-primary-blue"
          )}
        >
          {title}{" "}
          {isDisabled && (
            <p className="text-xs text-gray-400 mt-1">Already in carousel</p>
          )}
        </p>
        <div className="flex flex-wrap gap-x-4 gap-y-1 mt-1 text-sm text-secondary-blue">
          <span className="flex items-center gap-x-1.5">
            <Calendar className="w-3.5 h-3.5" />
            {dayDateLabel}
          </span>
          <span className="flex items-center gap-x-1.5">
            <Clock className="w-3.5 h-3.5" />
            {timeLabel}
          </span>
          {show.venue && (
            <span className="flex items-center gap-x-1.5">
              <MapPin className="w-3.5 h-3.5" />
              {show.venue}
            </span>
          )}
        </div>
      </div>
      <Checkbox
        checked={isSelected || isDisabled}
        disabled={isDisabled}
        className={cn(
          "border-2 border-neutral-300 data-[state=checked]:border-primary h-5 w-5",
          isDisabled && "opacity-50"
        )}
        onClick={(e) => e.stopPropagation()}
      />
    </button>
  );
}
